import { Component, DestroyRef, effect, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfigurationService } from '../../../shared/configuration/configuration.service';
import {
  CLIENT_NAME,
  CLIENT_NAMES,
} from '../../../shared/configuration/configuration.model';
import { Router } from '@angular/router';
import {
  AuthDialogMethod,
  AUTHORIZATION_STATE,
} from '../../auth/models/auth.models';
import { UserService } from 'src/app/shared/services/user.service';
import { map, Observable } from 'rxjs';
import { AuthComponent } from '../../auth/components/auth-modal/auth.component';
import { MatDialog } from '@angular/material/dialog';

import { AsyncPipe } from '@angular/common';
import { SupabaseUserToolsService } from 'src/app/shared/services/supabase-user-tools.service';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { HomepageWidgetsComponent } from 'src/app/shared/components/homepage-widgets/homepage-widgets.component';
import { AnnouncementsComponent } from '../../announcements/announcements.component';
import { SubscriptionBannerComponent } from '../../profile/subscription-banner/subscription-banner.component';
import { ClubService } from '../../../shared/services/club.service';
import { MatIcon } from '@angular/material/icon';
import {
  MatchmakingDialogComponent,
  MatchType,
  MatchConfirmEvent,
} from '../../../shared/components/matchmaking-dialog/matchmaking-dialog.component';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ActivelyLinkService } from '../../../shared/services/actively-link.service';
import { SnackBarService } from '../../../shared/services/snack-bar.service';
import { TranslationService } from '../../../shared/services/translation.service';
import { SponsorsCarouselComponent } from '../../../shared/components/sponsors-carousel/sponsors-carousel.component';
import { PlayerIdCardComponent } from '../../../shared/components/player-id-card/player-id-card.component';
import { DependentService } from '../../../shared/services/dependent.service';
import { PwaInstallBannerComponent } from '../../../shared/components/pwa-install-banner/pwa-install-banner.component';
import { MyNextMatchComponent } from '../../../shared/components/my-next-match/my-next-match.component';

@Component({
  selector: 'app-rules',
  templateUrl: './rules.component.html',
  styleUrl: './rules.component.scss',
  imports: [
    AsyncPipe,
    TranslatePipe,
    HomepageWidgetsComponent,
    AnnouncementsComponent,
    SubscriptionBannerComponent,
    MatIcon,
    MatchmakingDialogComponent,
    ProgressSpinnerModule,
    SponsorsCarouselComponent,
    PlayerIdCardComponent,
    PwaInstallBannerComponent,
    MyNextMatchComponent,
  ],
})
export class RulesComponent implements OnInit {
  readonly dialog = inject(MatDialog);
  private readonly dependentService = inject(DependentService);
  clientName!: string;
  subscriptionsFlagEnabled = false;
  activelyLinkEnabled = false;
  showSponsors = false;
  showPlayerId = false;
  showCalendar = true;
  showEventsTab = false;
  pushNotificationsEnabled = false;
  userFirstName = '';
  userId = '';
  userFullName = '';
  showMatchDialog = false;
  isSearching = false;
  matchType: MatchType | null = null;
  matchedReservationId: number | null = null;
  private destroyRef = inject(DestroyRef);
  public isLoggedIn = this.userService.isLoggedIn.pipe(
    map((authState) => authState === AUTHORIZATION_STATE.LOGGED_IN)
  );
  public isVerified$ = this.supabaseUserToolsService.isUserVerified$;

  constructor(
    private userService: UserService,
    private configurationService: ConfigurationService,
    private router: Router,
    private supabaseUserToolsService: SupabaseUserToolsService,
    private clubService: ClubService,
    private activelyLinkService: ActivelyLinkService,
    private snackBarService: SnackBarService,
    private translationService: TranslationService
  ) {
    effect(() => {
      const flags = this.clubService.flags();
      const theme = this.clubService.theme();
      const reservation_settings = this.clubService.reservationSettings();
      const subsEnabled = flags?.subscriptions;
      if (subsEnabled === undefined) {
        return;
      } else {
        this.subscriptionsFlagEnabled = subsEnabled;
        this.activelyLinkEnabled = flags?.actively_link ?? false;
        this.showSponsors = theme?.show_sponsors ?? false;
        this.showPlayerId = theme?.show_player_id ?? false;
        this.showCalendar = theme?.show_calendar !== false;
        this.showEventsTab = theme?.show_events_tab ?? false;
      }
    });

    this.destroyRef.onDestroy(() => {});
  }

  async ngOnInit() {
    this.clientName = this.configurationService.getClientName();
    await this.loadUserName();
    await this.checkPushNotificationStatus();
    await this.loadMatchmakingStatus();

    this.userService.isLoggedIn
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (state) => {
        if (state === AUTHORIZATION_STATE.NOT_LOGGED_IN) {
          this.userFirstName = '';
        } else if (state === AUTHORIZATION_STATE.LOGGED_IN) {
          await this.loadUserName();
        }
      });
  }

  private async loadUserName() {
    const user = await this.userService.getUserPromise();
    if (!user.data.user?.id) return;

    // Use effective user ID (could be a dependent)
    const effectiveId = this.dependentService.getEffectiveUserId();
    const userInfo =
      await this.userService.getUserInfoFromUsersTable(effectiveId);
    this.userId = effectiveId;
    if (userInfo.data && userInfo.data[0]?.fullname) {
      const fullname = userInfo.data[0].fullname.trim();
      this.userFirstName = fullname.split(' ')[0];
      this.userFullName = fullname;
    }
  }

  protected readonly CLIENT_NAMES = CLIENT_NAMES;

  /** Per-club homepage welcome message, sourced from the database
   *  (clubs.club_appearence.welcome_message via ClubService.theme()).
   *  Empty when the club hasn't set one — the template then shows nothing. */
  get welcomeMessage(): string {
    return this.clubService.theme()?.welcome_message?.trim() ?? '';
  }

  get showSuggestFeature(): boolean {
    return (
      this.clientName === CLIENT_NAMES.TRAININGROOM ||
      this.clientName === CLIENT_NAMES.ALPHA25
    );
  }

  onBookNow(logedIN: boolean) {
    if (logedIN) {
      if (this.showCalendar) {
        this.router.navigate(['calendar']);
      } else if (this.showEventsTab) {
        this.router.navigate(['tournaments']);
      }
      return;
    } else {
      const dialogRef = this.dialog.open(AuthComponent, {
        width: '440px',
        maxWidth: '95vw',
        panelClass: 'auth-dialog',
        data: {
          method: AuthDialogMethod.REGISTER,
        },
      });

      dialogRef.afterClosed().subscribe(() => {
        this.supabaseUserToolsService.updateUserStatus();
      });
    }
  }

  private async checkPushNotificationStatus() {
    const user = await this.userService.getUserPromise();
    const userId = user.data.user?.id;
    if (!userId) return;
    this.pushNotificationsEnabled =
      await this.supabaseUserToolsService.isUserSubscribedToPushNotifications(
        userId
      );
  }

  private async loadMatchmakingStatus() {
    const state = await this.activelyLinkService.getStatus();
    if (state.status === 'searching') {
      this.isSearching = true;
    } else if (state.status === 'matched') {
      this.isSearching = false;
      this.matchedReservationId = state.reservationId ?? null;
    }
  }

  onPlayToday() {
    this.showMatchDialog = true;
  }

  async onMatchConfirm(event: MatchConfirmEvent) {
    this.matchType = event.matchType;
    this.isSearching = true;
    this.showMatchDialog = false;

    try {
      const result = await this.activelyLinkService.register(event.matchType, event.favoritesOnly, event.facilityId);
      if (result.status === 'matched') {
        this.isSearching = false;
        this.matchedReservationId = result.reservationId ?? null;
      }
    } catch (err) {
      this.isSearching = false;
      this.matchType = null;
      this.snackBarService.openSnackBar(
        this.translationService.translate(
          'Something went wrong. Please try again later'
        )
      );
    }
  }

  async cancelSearch() {
    try {
      await this.activelyLinkService.unregister();
    } catch {
      // silent
    }
    this.isSearching = false;
    this.matchType = null;
  }

  protected suggestIdea =
    'https://docs.google.com/forms/d/e/1FAIpQLSdM61E6szuFOVYkyylotk9B_ViNYBch9JQtVSISRz4uhj7a4Q/viewform?usp=preview';

  navigateTo(url: string): void {
    window.open(url, '_blank');
  }
}
